<template>
    <div class="display-form">
        <flex-container
            size="full"
            height="full"
            :gap="Size.sm"
        >
            <flex-container
                direction="column"
                :gap="null"
            >
                <text-heading>Weergave</text-heading>
                <text-description>
                    Stel in of de chat widget automatisch opent, de aandacht trekt, en
                    alleen zichtbaar is op geselecteerde pagina’s.
                </text-description>
            </flex-container>

            <form-validation>
                <flex-container size="full" no-wrap>
                    <input-group
                        v-model="assistent.widget.openAfterSeconds"
                        :schema="number().required()"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Automatisch verwelkomen na:
                                </text-decorator>
                                <select-input
                                    :items="openOptions"
                                >
                                    <template #active="{ bindings }">
                                        <span v-if="bindings.value === 0">Nooit</span>
                                        <span v-else>
                                            {{ bindings.value }} {{ bindings.value > 1 ? 'seconden' : 'seconde' }}
                                        </span>
                                    </template>

                                    <template #item="{ bindings }">
                                        <span v-if="bindings.item === 0">Nooit</span>
                                        <span v-else>
                                            {{ bindings.item }} {{ bindings.item > 1 ? 'seconden' : 'seconde' }}
                                        </span>
                                    </template>
                                </select-input>
                            </flex-container>
                        </template>
                    </input-group>
                    <input-group
                        v-model="assistent.widget.hintAfterSeconds"
                        :schema="number().required()"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Aandacht trekken na:
                                </text-decorator>
                                <select-input
                                    :items="hintOptions"
                                >
                                    <template #active="{ bindings }">
                                        <span v-if="bindings.value === 0">Nooit</span>
                                        <span v-else>
                                            {{ bindings.value }} {{ bindings.value > 1 ? 'seconden' : 'seconde' }}
                                        </span>
                                    </template>

                                    <template #item="{ bindings }">
                                        <span v-if="bindings.item === 0">Nooit</span>
                                        <span v-else>
                                            {{ bindings.item }} {{ bindings.item > 1 ? 'seconden' : 'seconde' }}
                                        </span>
                                    </template>
                                </select-input>
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>
                <input-group
                    v-show="true"
                    v-model="assistent.widget.repeatAfterSeconds"
                    :schema="number().required()"
                >
                    <template #input>
                        <flex-container
                            :gap="Size.xs"
                            justify-content="flex-end"
                            align-items="center"
                        >
                            <select-input
                                :items="repeatOptions"
                                variant="inline"
                            >
                                <template #active="{ bindings }">
                                    <text-decorator>
                                        Herhaal na:
                                        <span v-if="bindings.value === 0" class="select-text">Nooit</span>
                                        <span v-else class="select-text">{{ bindings.value }} {{ bindings.value > 1 ? 'seconden' : 'seconde' }}</span>
                                    </text-decorator>
                                </template>
                                <template #item="{ bindings }">
                                    <span v-if="bindings.item === 0">Nooit</span>
                                    <span v-else>{{ bindings.item }} {{ bindings.item > 1 ? 'seconden' : 'seconde' }}</span>
                                </template>
                            </select-input>
                        </flex-container>
                    </template>
                </input-group>
            </form-validation>
            <horizontal-line no-margin />
            <input-group
                v-model="assistent.widget.filterMode"
                :items="FilterMode.values"
                :schema="object().required()"
            >
                <template #label>
                    <input-label>
                        Vertoning
                    </input-label>
                </template>
                <template #input>
                    <radio-group-input
                        label-key-name="label"
                        value-key-name="value"
                        :items="FilterMode.values"
                    />
                </template>
            </input-group>
            <input-group v-if="hasWinckBranding()" v-model="assistent.widget.showOnFlowPages">
                <template #input="{ bindings: checkboxBindings }">
                    <flex-container :gap="Size.sm" align-items="center">
                        <checkbox-input label="Toon op flow pagina's" />
                        <label :for="checkboxBindings.ids.input">
                            Toon op flow pagina's
                        </label>
                    </flex-container>
                </template>
            </input-group>
            <flex-container align-items="center" :gap="Size.sm">
                <svg-icon :icon="isEverywhere ? EyeSlashIcon : EyeIcon" is-custom-icon />
                <text-decorator>
                    {{ isEverywhere ? `Niet tonen op de volgende pagina's` : `Weergeven op de volgende pagina's:` }}
                </text-decorator>
            </flex-container>
            <display-at-form
                :filters="assistent.widget.filters as Filter[]"
                @filter:add="addFilter"
                @filter:delete="deleteFilter"
            />
        </flex-container>
    </div>
</template>

<script setup lang="ts">
    import {
        CheckboxInput,
        FlexContainer,
        FontSize,
        FormValidation,
        HorizontalLine,
        InputGroup,
        InputLabel,
        RadioGroupInput,
        SelectInput,
        Size,
        SvgIcon,
        TextDecorator,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';
    import { computed, type Ref, ref, watch } from 'vue';
    import { number, object } from 'yup';
    import { type Assistent, Filter, FilterMode } from '@keyvue/one-api-client';
    import EyeIcon from '@keyvue/one-awesome-icons/icons/regular/eye.svg?component';
    import EyeSlashIcon from '@keyvue/one-awesome-icons/icons/regular/eye-slash.svg?component';
    import DisplayAtForm from '@/ai/assistent/components/forms/display/DisplayAtForm.vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const { hasWinckBranding } = useAppBranding();

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    const isEverywhere = computed(() => {
        return assistent.value.widget.filterMode.value === FilterMode.EVERYWHERE.value;
    });

    const openOptions = [
        0,
        1,
        3,
        5,
        10,
    ];

    const hintOptions = [
        0,
        1,
        3,
        5,
        10,
    ];

    const repeatOptions = [
        0,
        1,
        3,
        5,
        10,
    ];

    function addFilter(filter: Filter) {
        assistent.value.widget.filters.push(filter);
    }

    function deleteFilter(index: number) {
        assistent.value.widget.filters.splice(index, 1);
    }
</script>

<style scoped lang="scss">
.select-text {
    color: var(--theme-colors-default--primary);
}
</style>
